import { TypeChecker } from '@attain-apps/type-guards'

import type { CurrentMonthBalance } from '../current-month-balance'
import type { DocumentLookup } from '../document-lookup'
import type { DocumentNumber } from '../document-number'
import { ExpenseCategory } from '../expense-category'
import type * as payment from '../payment'
import type { ProjectId } from '../project-id'

import { Text } from './text'

/**
 * One row of the BCH `DOC#` tab: a single project's current-month expenses,
 * the document they will be drawn against, and the overhead they attract.
 *
 * This is the grain the client's workbook calculates overhead at — one row per
 * project — which is why it exists as its own step rather than being folded
 * into the per-document {@link Row}. Overhead rounds here, and the document
 * total is the sum of those rounded figures.
 */
export interface Detail {
  readonly projectId: ProjectId
  readonly key: payment.Identifier
  readonly documentNumber: DocumentNumber
  /** Amounts by source category label, as the 53A reports name them. */
  readonly amountsByCategory: Readonly<Record<string, number>>
  /** `OH Rate` from the draw file's `PACCT` tab. */
  readonly ohRate: number
  /** `Overhead` — the modified total direct cost base at the OH rate. */
  readonly overhead: number
}
export namespace Detail {
  export const isDetail: TypeChecker<Detail> = TypeChecker.Factory.object({
    amountsByCategory: TypeChecker.Factory.record(
      TypeChecker.isString,
      TypeChecker.isNumber,
    ),
    documentNumber: TypeChecker.isString,
    key: TypeChecker.isString,
    ohRate: TypeChecker.isNumber,
    overhead: TypeChecker.isNumber,
    projectId: TypeChecker.isString,
  })

  /**
   * Builds the `DOC#` rows: one per project that resolved to a document.
   *
   * Projects with no document are dropped, which is the outline's "keep only
   * projects with Document #" step — they have nothing to draw against.
   */
  export function from(
    balances: readonly CurrentMonthBalance[],
    documentLookup: DocumentLookup,
  ): readonly Detail[] {
    const entryByProject = _entryByProject(documentLookup)
    const amountsByProject = new Map<ProjectId, Record<string, number>>()

    for (const balance of balances) {
      if (!entryByProject.has(balance.projectId)) {
        continue
      }
      const amounts = amountsByProject.get(balance.projectId) ?? {}
      amountsByProject.set(balance.projectId, amounts)
      amounts[balance.expenseCategory] =
        (amounts[balance.expenseCategory] ?? 0) + balance.amount
    }

    return [...amountsByProject.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .flatMap(([projectId, amountsByCategory]) => {
        const entry = entryByProject.get(projectId)
        if (!entry) {
          return []
        }
        /**
         * Documents can span projects and each project has its own rate, so the
         * document-level rate is only a fallback for a project the `PACCT` tab
         * did not price.
         */
        const ohRate = entry.ohRatesByProject?.[projectId] ?? entry.ohRate ?? 0
        return [
          {
            amountsByCategory,
            documentNumber: entry.documentNumber,
            key: entry.key,
            ohRate,
            overhead: Text.roundCurrency(
              _overheadBase(amountsByCategory) * ohRate,
            ),
            projectId,
          },
        ]
      })
  }

  /**
   * Sums the details of one document into source-category totals, the way the
   * client's `PIVOT` tab aggregates the `DOC#` tab.
   */
  export function totalsByCategory(
    details: readonly Detail[],
  ): Record<string, number> {
    const totals: Record<string, number> = {}
    for (const detail of details) {
      for (const [category, amount] of Object.entries(
        detail.amountsByCategory,
      )) {
        totals[category] = (totals[category] ?? 0) + amount
      }
    }
    return totals
  }

  /** Groups details by the document they will be drawn against. */
  export function byKey(
    details: readonly Detail[],
  ): ReadonlyMap<payment.Identifier, readonly Detail[]> {
    const byDocument = new Map<payment.Identifier, Detail[]>()
    for (const detail of details) {
      const existing = byDocument.get(detail.key)
      if (existing) {
        existing.push(detail)
      } else {
        byDocument.set(detail.key, [detail])
      }
    }
    return byDocument
  }

  /** The modified total direct cost base: the overhead-eligible categories. */
  function _overheadBase(amountsByCategory: Readonly<Record<string, number>>) {
    return Object.entries(amountsByCategory).reduce(
      (base, [category, amount]) =>
        ExpenseCategory.isOverheadEligible(category) ? base + amount : base,
      0,
    )
  }

  /**
   * Inverts the document lookup to a per-project index.
   *
   * A project belongs to one document, so the last document claiming it wins;
   * the lookup is built from the projects themselves, so this cannot conflict.
   */
  function _entryByProject(documentLookup: DocumentLookup) {
    const byProject = new Map<ProjectId, DocumentLookup.Entry>()
    for (const entry of Object.values(documentLookup)) {
      for (const projectId of entry.projectIds) {
        byProject.set(projectId, entry)
      }
    }
    return byProject
  }
}
